import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ClockAddIcon } from '@/shared/assets/icons/clock-add-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import { Can } from '@/shared/features/permissions/components/can/can';
import {
  TUpdateUserRoleLinkInputSchema,
  TUpdateUserRoleLinkProps,
} from '@/shared/features/rbac/components/update-user-role-link/update-user-role-link.types';
import {
  updateUserRoleLinkInputSchema,
  updateUserRoleLinkRequiredExpirationInputSchema,
} from '@/shared/features/rbac/components/update-user-role-link/update-user-role-link.validation';
import { UpdateUserRoleLinkForm } from '@/shared/features/rbac/components/update-user-role-link-form/update-user-role-link-form';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import {
  useReadUserRoleLinksForRoleQuery,
  useReadUserRoleLinksForUserQuery,
  useUpdateUserRoleLinkMutation,
} from '@/shared/redux/api/ident/ident-api';
import { showToastError } from '@/shared/utils/toast';

export const UpdateUserRoleLink: React.FC<TUpdateUserRoleLinkProps> = ({
  triggerComponent = 'MenuItem',
  onClose,
  onSuccess,
  users,
  userRoles,
}) => {
  const { t } = useTranslation();
  const { useTenantSettings, permissionActions } = useRbacConfig();
  const { maxUserRoleExpirationDays } = useTenantSettings();
  const { data: linksForUser } = useReadUserRoleLinksForUserQuery(
    { userId: users[0].userId! },
    { skip: users?.length !== 1 },
  );
  const { data: linksForRole } = useReadUserRoleLinksForRoleQuery(
    { userRoleId: userRoles[0].userRoleId! },
    { skip: userRoles?.length !== 1 },
  );
  const [updateUserRoleLink] = useUpdateUserRoleLinkMutation();

  const getUpdateUserRoleLinkSchema = () => {
    if (!!maxUserRoleExpirationDays && maxUserRoleExpirationDays > 0) {
      return updateUserRoleLinkRequiredExpirationInputSchema;
    }

    return updateUserRoleLinkInputSchema;
  };

  const onSubmit = async (
    data: TSubmittedData<TUpdateUserRoleLinkInputSchema>,
    onClose?: () => void,
  ) => {
    const count = data.data.links?.length || 0;
    try {
      const promises =
        data.data.links?.map(({ objectVersion, ...link }) =>
          updateUserRoleLink({
            ...link,
            objectVersion: objectVersion + 1,
          }).unwrap(),
        ) || [];
      await Promise.all(promises);
      toast.success(t('rbac:update_user_role_link_success', { count }));
      onSuccess?.();
      onClose?.();
    } catch {
      showToastError(t('rbac:update_user_role_link_error', { count }));
    }
  };

  const getDefaultValues = () => {
    if (users?.length === 1 && linksForUser) {
      return {
        links: linksForUser.readUserRoleUserLinksForUser.items?.filter(
          ({ userRoleId }) =>
            userRoles
              ?.map((userRole) => userRole.userRoleId)
              .includes(userRoleId),
        ),
      };
    }

    if (userRoles?.length === 1 && linksForRole) {
      return {
        links: linksForRole.readUserRoleUserLinksForUserRole.items?.filter(
          ({ userId }) => users?.map((user) => user.userId).includes(userId),
        ),
      };
    }

    return undefined;
  };

  return (
    <Can
      action={permissionActions.linkUserRole}
      ids={userRoles.map((role) => role.userRoleId!)}
    >
      <DrawerController
        trigger={
          <Trigger
            icon={<ClockAddIcon />}
            component={triggerComponent}
            buttonVariant="secondary"
            buttonSize="extraSmall"
            label={t('rbac:update_expiration_date')}
          />
        }
        title={t('rbac:update_expiration_date')}
        showDivider={false}
      >
        {(closeDrawer) => {
          const handleClose = () => {
            closeDrawer();
            onClose?.();
          };
          return (
            <Form<TUpdateUserRoleLinkInputSchema>
              schema={getUpdateUserRoleLinkSchema()}
              onSubmit={(data) => onSubmit(data, handleClose)}
              defaultValues={getDefaultValues()}
            >
              {() => (
                <UpdateUserRoleLinkForm
                  users={users}
                  userRoles={userRoles}
                  onClose={handleClose}
                />
              )}
            </Form>
          );
        }}
      </DrawerController>
    </Can>
  );
};
